import { ComponentType, UserDTO } from "groopit-js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { initNoteComponentEditor } from "./NoteDataEditorJQueryInitializer.js";

interface NoteDataEditorProps {
  description: string;
  parentComponent?: ComponentType;
  disabled: boolean;
  baseName: string;
  groupId: string;
  tags?: { [id: string]: UserDTO };
  rawText?: string;
}

export const NoteDataEditor = (props: NoteDataEditorProps) => {
  const {
    description,
    parentComponent,
    disabled,
    baseName,
    groupId,
    tags,
    rawText,
  } = {
    ...props,
  };

  const [textArea, setTextArea] = useState<HTMLDivElement>();

  const name = `${baseName}.Notes`;
  // Basic implementation of Razor's HtmlHelper.IdFor:
  // Replaces . and [ and ] with _ for use in an id attribute
  const id = name.replace(/[[\].]/g, "_");

  // Using contentEditable is a little tricky because we don't want React to
  // constantly re-render or to get out of sync with its shadow DOM so we
  // don't ever bind the text directly. We take the raw text as input and
  // encode it into HTML, then listen for any changes in the HTML and decode
  // it back into raw text using a different state variable that only drives
  // the value on the input control.
  const taggedText = useMemo(() => {
    const textArea = document.createElement("textarea");
    textArea.innerText = rawText ?? "";
    let encodedText = textArea.innerHTML.split("<br>").join("\n");
    if (tags) {
      Object.keys(tags).forEach((userId) => {
        encodedText = encodedText.replace(
          "@" + userId,
          "<input class='tag ping' type='button' value='@" +
            tags[userId].friendlyName +
            "' + data-user-id='" +
            userId +
            "'></input>",
        );
      });
    }
    return encodedText;
  }, [rawText, tags]);

  const [textToSubmit, setTextToSubmit] = React.useState(rawText ?? "");

  useEffect(() => {
    setTextToSubmit(rawText ?? "");
  }, [rawText]);

  const handleTextChange = useCallback(() => {
    if (textArea) {
      const clone = textArea.cloneNode(true) as HTMLDivElement;

      //
      // Replace any tag pings with the actual user ID
      //
      clone.querySelectorAll("input.tag.ping").forEach((tag) => {
        tag.replaceWith("@" + tag.getAttribute("data-user-id"));
      });

      //
      // Since the cloned node is not visible, we cannot just use .innerText.
      // We have to manually parse out the <div> and <br> tags and replace them
      // with appropriate \n characters.
      //

      //
      // Remove any trailing <br> tags since
      // <div><br></div> is just one newline not two
      //
      clone.querySelectorAll("br:last-child").forEach((newline) => {
        newline.replaceWith("");
      });

      //
      // Any remaining <br> tags should be replaced with \n
      //
      clone.querySelectorAll("br").forEach((newline) => {
        newline.replaceWith("\n");
      });

      // combine adjacent text nodes into a single text node
      clone.normalize();

      // Now we should have a list of text nodes and div nodes that we can
      // combine into a single string.
      const text = [...clone.childNodes]
        .map((node) => node.textContent)
        .join("\n");
      setTextToSubmit(text);

      if (textArea) {
        textArea.style.height = "auto";
        textArea.style.height = textArea.scrollHeight + 25 + "px";
      }
    }
  }, [textArea]);

  useEffect(() => {
    if (textArea) {
      textArea.style.height = textArea.scrollHeight + "px";

      // Hooking the onChange effect doesn't seem to work in JSX
      textArea.onchange = handleTextChange;

      // Ideally, we'd replace this with a React component but
      // this library isn't really maintained anymore so I'm
      // just going to call into the old JS code to initialize it
      // and let it do its thing for now.
      initNoteComponentEditor(textArea);
    }
  }, [textArea, handleTextChange]);

  return (
    <>
      <input type="hidden" name={name} id={id} value={textToSubmit} />
      {parentComponent == ComponentType.Table ? (
        <div className="note-autocomplete h-full">
          <div
            id={`noteshadow_${id}`}
            ref={(ref) => setTextArea(ref ?? undefined)}
            data-target={`#${id}`}
            data-user-search-url={`/Groop/SearchUsers/${groupId}`}
            className="textarea notecomponent w-full whitespace-pre-wrap overflow-y-hidden h-full"
            contentEditable={disabled ? "false" : "true"}
            dangerouslySetInnerHTML={{ __html: taggedText }}
            onInput={handleTextChange}
          ></div>
        </div>
      ) : (
        <div className="flex space-x-3 post-component">
          <img
            src="/Content/Images/ic_notes.png"
            className="groopit-component-icon-sm"
          />
          <div className="flex-grow note-autocomplete">
            <label
              htmlFor={id}
              className="text-display-normal text-color-secondary notes"
            >
              {description}
            </label>
            <div
              id={`noteshadow_${id}`}
              ref={(ref) => setTextArea(ref ?? undefined)}
              data-target={`#${id}`}
              data-user-search-url={`/Groop/SearchUsers/${groupId}`}
              className="textarea notecomponent form-control overflow-y-auto whitespace-pre-wrap"
              contentEditable={disabled ? "false" : "true"}
              dangerouslySetInnerHTML={{ __html: taggedText }}
              onInput={handleTextChange}
            ></div>
          </div>
        </div>
      )}
    </>
  );
};
